import { useState } from "react";
import { tvApi } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tv, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

/**
 * ConnectTVDialog — host enters the 6-char code shown on the TV
 * and selects which event to display. Sends the pairing request,
 * which makes the TV auto-redirect to its slideshow.
 */
export default function ConnectTVDialog({ open, onOpenChange, events = [] }) {
  const [step, setStep] = useState("code"); // code | event | sending | done | error
  const [code, setCode] = useState("");
  const [eventId, setEventId] = useState("");
  const [error, setError] = useState(null);

  const reset = () => {
    setStep("code");
    setCode("");
    setEventId("");
    setError(null);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange?.(v);
  };

  const cleanCode = (raw) => (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);

  const handleCodeSubmit = (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      setError("Codigo deve ter 6 caracteres");
      return;
    }
    setError(null);
    setStep("event");
  };

  const handlePair = async (chosenEventId) => {
    setStep("sending");
    setError(null);
    try {
      await tvApi.pair(code, chosenEventId);
      setStep("done");
      // Auto-close after 2s
      setTimeout(() => handleClose(false), 2000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao conectar TV. Verifique o codigo.";
      setError(msg);
      setStep("error");
    }
  };

  const codeDisplay = code.length > 3 ? `${code.slice(0, 3)}-${code.slice(3)}` : code;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl max-w-md mx-auto p-0 overflow-hidden"
        data-testid="connect-tv-dialog"
      >
        <div className="bg-gradient-to-r from-[#1C1917] to-[#44403C] p-5 text-white">
          <DialogHeader className="space-y-0">
            <div className="flex items-center gap-2 mb-1">
              <Tv className="w-5 h-5" strokeWidth={2.5} />
              <DialogTitle
                className="text-lg font-black text-white"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                Conectar TV
              </DialogTitle>
            </div>
            <p className="text-sm text-white/80">
              Abra <strong>festaia.com.br/tv</strong> na sua TV e digite o codigo que aparecer aqui.
            </p>
          </DialogHeader>
        </div>

        <div className="p-5">
          {step === "code" && (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#78716C] mb-2">
                  Codigo da TV
                </label>
                <Input
                  type="text"
                  value={codeDisplay}
                  onChange={(e) => setCode(cleanCode(e.target.value))}
                  placeholder="ABC-D23"
                  className="w-full text-center text-3xl font-black tracking-[0.3em] py-6 border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-xl font-mono"
                  autoFocus
                  autoComplete="off"
                  autoCapitalize="characters"
                  data-testid="tv-pair-code-input"
                />
                <p className="text-xs text-[#78716C] mt-2">
                  Voce ve o codigo na tela da TV apos abrir <code>festaia.com.br/tv</code>
                </p>
              </div>
              {error && <p className="text-sm text-red-600 font-bold">{error}</p>}
              <Button
                type="submit"
                disabled={code.length !== 6}
                className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 text-base hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all disabled:opacity-50"
                data-testid="tv-pair-submit"
              >
                Continuar
              </Button>
            </form>
          )}

          {step === "event" && (
            <div className="space-y-3">
              <p className="text-sm text-[#44403C] font-bold mb-1">
                Codigo: <span className="font-mono text-[#FF6B6B] tracking-wider">{codeDisplay}</span>
              </p>
              <p className="text-xs text-[#78716C] mb-3">
                Qual evento deseja exibir na TV?
              </p>
              {events.length === 0 ? (
                <p className="text-sm text-[#78716C] text-center py-6">
                  Voce ainda nao tem eventos. Crie um primeiro.
                </p>
              ) : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                  {events.map((ev) => (
                    <button
                      key={ev.event_id}
                      onClick={() => handlePair(ev.event_id)}
                      className="w-full text-left p-3 border-2 border-[#1C1917] rounded-xl shadow-[3px_3px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_#1C1917] active:translate-y-[2px] active:shadow-none transition-all bg-white"
                      data-testid={`tv-pair-event-${ev.event_id}`}
                    >
                      <p className="font-black text-[#1C1917]">{ev.name}</p>
                      {ev.date && <p className="text-xs text-[#78716C]">{ev.date}</p>}
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => setStep("code")}
                className="w-full mt-3 text-sm text-[#78716C] hover:text-[#1C1917] font-medium py-2"
              >
                Voltar
              </button>
            </div>
          )}

          {step === "sending" && (
            <div className="py-8 text-center">
              <Loader2 className="w-10 h-10 animate-spin text-[#FF6B6B] mx-auto mb-3" />
              <p className="font-bold text-[#1C1917]">Conectando a TV...</p>
            </div>
          )}

          {step === "done" && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 border-2 border-[#1C1917] rounded-full flex items-center justify-center mx-auto mb-3 shadow-[2px_2px_0px_#1C1917]">
                <CheckCircle2 className="w-8 h-8 text-green-600" strokeWidth={2.5} />
              </div>
              <p className="font-black text-[#1C1917] text-lg mb-1">TV conectada!</p>
              <p className="text-sm text-[#78716C]">A TV ja esta exibindo o evento.</p>
            </div>
          )}

          {step === "error" && (
            <div className="py-6 text-center space-y-3">
              <AlertTriangle className="w-10 h-10 text-red-600 mx-auto" strokeWidth={2.5} />
              <p className="font-bold text-[#1C1917]">{error}</p>
              <Button
                onClick={reset}
                className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3"
              >
                Tentar de novo
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
